/**
 * The storefront's RETRY POLICY — one rule, stated once, for every TanStack query.
 *
 * ── WHY THIS FILE EXISTS ───────────────────────────────────────────────────
 * A live QA sweep (2026-08-05) recorded the storefront firing **767
 * `GET /bff/funnel` requests in 14.4 seconds** while holding a session cookie the
 * BFF no longer knew: 30 × `401`, then 737 × `429`. The same cookie replayed by a
 * single `curl` returns one clean `401` — so the amplification was entirely
 * client-side. The customer then could not recover, because the per-IP baseline
 * limiter is shared and the recovery call (`GET /bff/session`) was itself `429`'d.
 *
 * Part of that was a blanket `retry: 1` on the query client, which asked EVERY
 * failing call a second time — including calls whose answer cannot change.
 *
 * ── THE RULE: A 4xx IS AN ANSWER, NOT AN OUTAGE ────────────────────────────
 * A `401` ("you have no funnel session"), a `403` ("captcha required"), a `404`
 * ("no such plan") and a `429` ("you are over your allowance") are all the server
 * telling us something TRUE about this request. Asking again cannot change any of
 * them; on a public, IP-bucketed edge it can only spend the customer's remaining
 * allowance on an answer we already have — which is precisely how a lapsed session
 * turned into a self-inflicted denial of service for everyone behind the same NAT.
 *
 * A 5xx or a transport failure IS potentially transient, so those keep exactly one
 * retry — the storefront's existing posture, unchanged.
 *
 * ⚠️ THIS IS A CEILING, NOT A LICENCE. Individual hooks may still be stricter
 * (`funnel.queries`, `esiid.queries`, `serviceability.queries` and `estimate.queries`
 * all pin `retry: false` because a client retry would eat a rate-limited free
 * allowance). Nothing here relaxes those.
 */

/** Transient failures (5xx / transport) are retried at most this many times. */
export const MAX_TRANSIENT_RETRIES = 1;

/**
 * The HTTP status carried by a failure, when it carries one.
 *
 * Deliberately STRUCTURAL rather than `instanceof BffError`: this is app-level
 * config and must not reach down into a module's API layer to do its job. A
 * narrowing `in` check plus a `typeof` guard means no cast is needed — an error
 * with a non-numeric `status` (or none at all) reads as `undefined` rather than
 * being coerced into a status it does not have.
 */
export function httpStatusOf(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null || !('status' in error)) return undefined;
  const status: unknown = error.status;
  return typeof status === 'number' ? status : undefined;
}

/**
 * True when the failure is a CLIENT error (4xx) — a definitive answer about this
 * request that a repeat cannot change.
 */
export function isTerminalClientFailure(error: unknown): boolean {
  const status = httpStatusOf(error);
  return status !== undefined && status >= 400 && status < 500;
}

/**
 * The TanStack `retry` predicate. Never retries a 4xx; otherwise allows up to
 * `MAX_TRANSIENT_RETRIES`.
 */
export function shouldRetryStorefrontQuery(failureCount: number, error: unknown): boolean {
  if (isTerminalClientFailure(error)) return false;
  return failureCount < MAX_TRANSIENT_RETRIES;
}
